/**
 * Text that will be printed onto PDFs with the standard fonts, which only cover
 * Latin-1. Shared by the server (stamping) and the browser (preview), so what
 * the client sees is exactly what gets printed.
 */

// Letters with no Latin-1 form and no accent to strip.
const LATIN_MAP: Record<string, string> = { Ł: "L", ł: "l", Đ: "D", đ: "d", Ħ: "H", ħ: "h", ı: "i", Ŀ: "L", ŀ: "l", Ŧ: "T", ŧ: "t", Œ: "OE", œ: "oe", ß: "ss" };

/** Maps text to Latin-1; characters that can't be printed become "?". */
export function toPrintable(text: string): string {
  let out = "";
  for (const ch of text
    .normalize("NFC")
    .replace(/[–—−]/g, "-")
    .replace(/[‘’]/g, "'")
    .replace(/[“”]/g, '"')
    .replace(/…/g, "...")) {
    if (/[\x20-\x7e\xa0-\xff]/.test(ch)) out += ch;
    else if (LATIN_MAP[ch]) out += LATIN_MAP[ch];
    else {
      // "ă" → "a": drop the accent when the base letter is printable.
      const base = ch.normalize("NFKD").replace(/[̀-ͯ]/g, "");
      out += base && /^[\x20-\x7e\xa0-\xff]+$/.test(base) ? base : "?";
    }
  }
  return out;
}

/** True when every character can be printed as typed (or with a close Latin form). */
export function isPrintable(text: string): boolean {
  return !toPrintable(text.replace(/\?/g, "")).includes("?");
}

/** "Mary-Ann Østergård" → "MAØ". Empty when the name has no printable letters. */
export function initialsOf(name: string): string {
  return toPrintable(name)
    .split(/[\s-]+/)
    .map((w) => [...w].find((c) => /\p{L}/u.test(c)) ?? "")
    .join("")
    .toUpperCase()
    .slice(0, 4);
}
